import { directSupabase } from '../lib/supabaseClient';
import { fromDbAccount, type DirectoryAccount } from '../modules/accounts/accounts.model';
import { accountsService } from '../modules/accounts/accounts.service';
import { fromDbBill, type AccountBill } from '../modules/accounts/bills.model';
import type { CISInvoiceItem, CISMonthlyPeriod, CISSummary } from '../types/cis.types';
import { billToService, type BillToCompany } from './billToService';

/** Today in the UK, as YYYY-MM-DD. */
export function ukToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(now);
}

/**
 * A subcontractor sees a tax month only once it has ended (after the 5th), matching
 * my_cis_statements() in the database, which is what actually enforces it.
 */
export function isCisPeriodClosed(periodEnd: string, today: string = ukToday()): boolean {
  return periodEnd < today;
}

/**
 * A bill as the CIS summary and certificates see it. Bills copied from the old subcontractor
 * invoices keep their original lines and CIS rate, so historic figures are unchanged.
 */
export function cisItemFromBill(
  bill: AccountBill,
  accountsById: Map<string, DirectoryAccount>,
  billToById: Map<string, string>,
): CISInvoiceItem {
  const account = accountsById.get(bill.accountId);
  const legacy = bill.legacySnapshot;
  const subtotal = bill.totals.netTotal;
  const cisPercent = legacy
    ? Number(legacy.cis_percent) || 0
    : bill.totals.cisRate != null ? Math.round(bill.totals.cisRate * 1000) / 10 : 0;
  return {
    id: bill.id,
    subcontractor_id: bill.accountId,
    invoice_date: bill.datedOn,
    period_date: bill.paidOn || bill.datedOn,
    invoice_number: bill.reference,
    subcontractor_name: account?.contactName || account?.companyName || String(legacy?.issued_by || 'Unknown'),
    subcontractor_company: account?.companyName ?? null,
    subcontractor_address: account?.address ?? null,
    subcontractor_utr_number: account?.utrNumber ?? null,
    subcontractor_ni_number: account?.niNumber ?? null,
    subcontractor_verification_number: account?.cisVerificationNumber ?? null,
    description: bill.description,
    line_items: legacy && Array.isArray(legacy.line_items)
      ? legacy.line_items
      : bill.lines.map((line) => ({
        description: line.description,
        quantity: line.quantity,
        unit_price: line.unitPrice,
        total: line.net,
        // Only labour carries a deduction; materials and other lines do not.
        item_type: line.kind === 'labour' ? 'labour' as const : 'material' as const,
      })),
    subtotal,
    cis_percent: cisPercent,
    cis_deduction: bill.totals.cisDeduction,
    net_total: subtotal - bill.totals.cisDeduction,
    to_company_name: (bill.billToCompanyId && billToById.get(bill.billToCompanyId)) || legacy?.to_company_name || null,
  };
}

export const cisService = {
  /**
   * Get CIS summary data grouped by monthly periods
   * HMRC CIS periods: 6th of one month to 5th of next month (both dates inclusive)
   */
  async getCISSummary(): Promise<CISSummary> {
    const [{ data: billRows, error: billsError }, accounts, billToCompanies] = await Promise.all([
      directSupabase.from('account_bills').select('*').order('dated_on', { ascending: false }),
      accountsService.list(),
      billToService.listActive().catch(() => [] as BillToCompany[]),
    ]);
    if (billsError) {
      throw new Error(`Failed to fetch CIS bills: ${billsError.message}`);
    }
    const accountsById = new Map(accounts.map((account) => [account.id, account]));
    const billToById = new Map(billToCompanies.map((company) => [company.id, company.name]));

    const cisInvoices = (billRows || [])
      .map((row) => cisItemFromBill(fromDbBill(row), accountsById, billToById))
      .filter((item) => item.cis_percent > 0);
    return this.summarise(cisInvoices);
  },

  /**
   * The signed-in Keystone subcontractor's own CIS statements, from closed tax months only.
   * `account` is null when their sign-in email is not linked to an account.
   */
  async getMyCISSummary(): Promise<{ account: DirectoryAccount | null; summary: CISSummary }> {
    const [{ data, error }, billToCompanies] = await Promise.all([
      directSupabase.rpc('my_cis_statements'),
      billToService.listActive().catch(() => [] as BillToCompany[]),
    ]);
    if (error) {
      if (error.code === '42883' || error.code === 'PGRST202') {
        throw new Error('CIS certificates are not set up yet. Ask the office to apply the subcontractor CIS migration.');
      }
      throw new Error(`Failed to load your CIS statements: ${error.message}`);
    }
    const payload = (data || {}) as { account?: Record<string, any> | null; bills?: Array<Record<string, any>> };
    const account = payload.account ? fromDbAccount(payload.account) : null;
    const accountsById = new Map(account ? [[account.id, account]] : []);
    const billToById = new Map(billToCompanies.map((company) => [company.id, company.name]));
    const items = (payload.bills || [])
      .map((row) => cisItemFromBill(fromDbBill(row), accountsById, billToById))
      .filter((item) => item.cis_percent > 0)
      .filter((item) => {
        const [, periodEnd] = this.parsePeriodKey(this.getMonthlyPeriodKey(new Date(item.period_date || item.invoice_date)));
        return isCisPeriodClosed(periodEnd);
      });
    return { account, summary: this.summarise(items) };
  },

  summarise(cisInvoices: CISInvoiceItem[]): CISSummary {
    // Group by monthly periods
    const monthlyPeriods = this.groupByMonthlyPeriods(cisInvoices);

    // Calculate grand totals
    const grandTotalGross = monthlyPeriods.reduce((sum, period) => sum + period.total_gross, 0);
    const grandTotalCisDeduction = monthlyPeriods.reduce((sum, period) => sum + period.total_cis_deduction, 0);
    const grandTotalNet = monthlyPeriods.reduce((sum, period) => sum + period.total_net, 0);

    return {
      monthly_periods: monthlyPeriods,
      grand_total_gross: grandTotalGross,
      grand_total_cis_deduction: grandTotalCisDeduction,
      grand_total_net: grandTotalNet
    };
  },

  /**
   * Group invoices by monthly periods (6th to 5th of next month, inclusive)
   * HMRC CIS periods: 6th of one month to 5th of next month (both dates inclusive)
   */
  groupByMonthlyPeriods(invoices: CISInvoiceItem[]): CISMonthlyPeriod[] {
    const periodMap = new Map<string, CISInvoiceItem[]>();

    invoices.forEach(invoice => {
      const invoiceDate = new Date(invoice.period_date || invoice.invoice_date);
      const periodKey = this.getMonthlyPeriodKey(invoiceDate);
      
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, []);
      }
      periodMap.get(periodKey)!.push(invoice);
    });

    // Convert to array and sort by period start date (newest first)
    const periods: CISMonthlyPeriod[] = Array.from(periodMap.entries()).map(([key, periodInvoices]) => {
      const [startDate, endDate] = this.parsePeriodKey(key);
      
      // Sort invoices within period by date (newest first)
      periodInvoices.sort((a, b) => new Date(b.invoice_date).getTime() - new Date(a.invoice_date).getTime());

      const totalGross = periodInvoices.reduce((sum, inv) => sum + inv.subtotal, 0);
      const totalCisDeduction = periodInvoices.reduce((sum, inv) => sum + inv.cis_deduction, 0);
      const totalNet = periodInvoices.reduce((sum, inv) => sum + inv.net_total, 0);

      return {
        period_start: startDate,
        period_end: endDate,
        period_label: this.formatPeriodLabel(startDate, endDate),
        invoices: periodInvoices,
        total_gross: totalGross,
        total_cis_deduction: totalCisDeduction,
        total_net: totalNet
      };
    });

    // Sort periods by start date (newest first)
    periods.sort((a, b) => new Date(b.period_start).getTime() - new Date(a.period_start).getTime());

    return periods;
  },

  /**
   * Get the period key for a given date
   * Periods run from 6th of one month to 5th of next month (inclusive)
   * HMRC CIS periods: 6th to 5th of next month
   */
  getMonthlyPeriodKey(date: Date): string {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-based
    const day = date.getDate();

    let periodStartMonth = month;
    let periodStartYear = year;

    if (day >= 6) {
      // If day is 6th or later, period starts this month
      periodStartMonth = month;
      periodStartYear = year;
    } else {
      // If day is 1st-5th, period started last month
      if (month === 0) {
        periodStartMonth = 11; // December
        periodStartYear = year - 1;
      } else {
        periodStartMonth = month - 1;
        periodStartYear = year;
      }
    }

    // Calculate period start: 6th of the period start month
    const periodStart = new Date(periodStartYear, periodStartMonth, 6);
    
    // Calculate period end: 5th of the next month
    // Handle year rollover properly
    let periodEndMonth = periodStartMonth + 1;
    let periodEndYear = periodStartYear;
    
    if (periodEndMonth > 11) {
      periodEndMonth = 0; // January
      periodEndYear = periodStartYear + 1;
    }
    
    const periodEnd = new Date(periodEndYear, periodEndMonth, 5);

    // Format dates as YYYY-MM-DD without timezone issues
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return `${formatDate(periodStart)}_${formatDate(periodEnd)}`;
  },

  /**
   * Parse period key back to start and end dates
   */
  parsePeriodKey(key: string): [string, string] {
    const [start, end] = key.split('_');
    return [start, end];
  },

  /**
   * Format period label for display
   */
  formatPeriodLabel(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startMonth = start.toLocaleDateString('en-GB', { month: 'long' });
    const endMonth = end.toLocaleDateString('en-GB', { month: 'long' });
    const year = start.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${year}`;
    } else {
      return `${startMonth} - ${endMonth} ${year}`;
    }
  }
};
